// lib/cart/flyToCart.ts
// "Fly to cart" add feedback: a small brand-colored dot detaches from the
// clicked button and arcs to the host page's cart icon — the same gesture
// syfak's own listing pages use (founder request, 2026-07-27: our adds were
// visually silent). Browser-only.
//
// Where it renders: the ghost is appended to the widget's own root (the shadow
// root on an embed, document.body on the demo pages). Inside the embed that
// root lives in the SearchModal <dialog>'s top layer, so a position:fixed ghost
// can travel across the whole viewport — including OVER the host's header,
// where the cart icon sits visible above our modal (verified on syfak staging).
//
// Target: `targetSelector` is queried against the HOST document (crossing the
// shadow boundary outward is fine) — for syfak that's `#cart`, their header
// basket (inspected live 2026-07-27, with its `.cart_counter` badge). On pages
// without the host header (our /demo/*), a configured-but-missing target falls
// back to the top-right corner, where a shop's cart lives — the gesture still
// reads as "went to the cart". No selector configured → no ghost (clients that
// haven't opted in are untouched).

export function flyToCart(fromEl: HTMLElement, targetSelector?: string | null): void {
  try {
    if (!targetSelector || typeof window === 'undefined' || typeof document === 'undefined') return;
    // Feedback is decoration — never animate for reduced-motion users.
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const from = fromEl.getBoundingClientRect();
    if (from.width === 0 && from.height === 0) return;

    const target = document.querySelector(targetSelector);
    const to = target?.getBoundingClientRect();
    const hasTarget = !!(to && (to.width > 0 || to.height > 0));
    const toX = hasTarget ? to!.x + to!.width / 2 : window.innerWidth - 40;
    const toY = hasTarget ? to!.y + to!.height / 2 : 24;

    // Same root as the widget → same top layer as the modal on the embed.
    const root = fromEl.getRootNode();
    const container: ShadowRoot | HTMLElement = root instanceof ShadowRoot ? root : document.body;

    // Concrete brand color: CSS vars on the widget container don't reach a
    // sibling ghost node, so resolve it from the button we flew from.
    const brand = getComputedStyle(fromEl).getPropertyValue('--color-brand').trim() || '#0a8a5f';

    const size = 16;
    const startX = from.x + from.width / 2 - size / 2;
    const startY = from.y + from.height / 2 - size / 2;
    const ghost = document.createElement('div');
    ghost.style.cssText =
      `position:fixed;left:${startX}px;top:${startY}px;width:${size}px;height:${size}px;` +
      `border-radius:50%;background:${brand};box-shadow:0 2px 8px rgba(0,0,0,0.35);` +
      'z-index:2147483647;pointer-events:none;';
    container.appendChild(ghost);

    const dx = toX - (startX + size / 2);
    const dy = toY - (startY + size / 2);
    const animation = ghost.animate(
      [
        { transform: 'translate(0,0) scale(1)', opacity: 1 },
        // Midpoint lifted 40px for a light arc, like a tossed item.
        { transform: `translate(${dx * 0.5}px,${dy * 0.55 - 40}px) scale(0.9)`, opacity: 0.95, offset: 0.55 },
        { transform: `translate(${dx}px,${dy}px) scale(0.25)`, opacity: 0.25 },
      ],
      { duration: 700, easing: 'cubic-bezier(0.35,0,0.25,1)' },
    );
    const cleanup = () => ghost.remove();
    animation.onfinish = cleanup;
    animation.oncancel = cleanup;
    // Belt & braces: never leave a stray dot if the animation never settles.
    window.setTimeout(cleanup, 1500);
  } catch {
    // Feedback must never break the add itself.
  }
}
